import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import { enablementReportsService } from '../../../services/enablementService';
import { partnerService } from '../../../services/partnerService';
import { useToast } from '../../../contexts/ToastContext';
import { exportToCSV, exportToPDF } from '../../../utils/exportUtils';

const REPORT_TYPES = [
  { id: 'training', label: 'Training Records', icon: 'GraduationCap', color: 'from-amber-500 to-orange-600' },
  { id: 'certifications', label: 'Certifications', icon: 'Award', color: 'from-teal-500 to-cyan-600' },
  { id: 'assignments', label: 'Training Assignments', icon: 'ClipboardList', color: 'from-violet-500 to-indigo-600' },
  { id: 'capability', label: 'Capability Assessments', icon: 'BarChart3', color: 'from-indigo-500 to-violet-600' },
];

const COLUMNS = {
  training: ['name', 'partner_name', 'training_status', 'progress', 'duration', 'participants', 'expiry_date'],
  certifications: ['name', 'partner_name', 'issuer', 'cert_status', 'cert_level', 'expiry_date'],
  assignments: ['training_title', 'partner_name', 'partner_tier', 'assignment_status', 'due_date', 'completion_date'],
  capability: ['partner_name', 'partner_tier', 'overall_score', 'technical_score', 'sales_score', 'training_score', 'delivery_score', 'support_score', 'assessment_date'],
};

const HEADERS = {
  training: { name: 'Training Name', partner_name: 'Partner', training_status: 'Status', progress: 'Progress %', duration: 'Duration', participants: 'Participants', expiry_date: 'Expiry Date' },
  certifications: { name: 'Certification', partner_name: 'Partner', issuer: 'Issuer', cert_status: 'Status', cert_level: 'Level', expiry_date: 'Expiry Date' },
  assignments: { training_title: 'Training', partner_name: 'Partner', partner_tier: 'Tier', assignment_status: 'Status', due_date: 'Due Date', completion_date: 'Completed' },
  capability: { partner_name: 'Partner', partner_tier: 'Tier', overall_score: 'Overall', technical_score: 'Technical', sales_score: 'Sales', training_score: 'Training', delivery_score: 'Delivery', support_score: 'Support', assessment_date: 'Date' },
};

const flattenRow = (row, type) => {
  if (type === 'training') return { ...row, partner_name: row?.partners?.brand_name || row?.partners?.legal_name || '' };
  if (type === 'certifications') return { ...row, partner_name: row?.partners?.brand_name || row?.partners?.legal_name || '' };
  if (type === 'assignments') return { ...row, training_title: row?.training_catalog?.title || '', partner_name: row?.partners?.brand_name || row?.partners?.legal_name || '', partner_tier: row?.partners?.tier || '' };
  if (type === 'capability') return { ...row, partner_name: row?.partners?.brand_name || row?.partners?.legal_name || '', partner_tier: row?.partners?.tier || '' };
  return row;
};

const statusBadge = (status) => {
  const map = { completed: 'bg-emerald-100 text-emerald-700', active: 'bg-emerald-100 text-emerald-700', 'in-progress': 'bg-amber-100 text-amber-700', in_progress: 'bg-amber-100 text-amber-700', required: 'bg-red-100 text-red-700', expired: 'bg-red-100 text-red-700', assigned: 'bg-blue-100 text-blue-700', pending: 'bg-slate-100 text-slate-600' };
  return map?.[status] || 'bg-slate-100 text-slate-600';
};

const EnablementReports = () => {
  const toast = useToast();
  const [reportType, setReportType] = useState('training');
  const [data, setData] = useState([]);
  const [partners, setPartners] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filterPartner, setFilterPartner] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');

  useEffect(() => { partnerService?.getAll()?.then(p => setPartners(p || []))?.catch(() => {}); }, []);
  useEffect(() => { loadReport(); }, [reportType, filterPartner, filterStatus]);

  const loadReport = async () => {
    setLoading(true);
    try {
      const filters = { partnerId: filterPartner !== 'all' ? filterPartner : undefined, status: filterStatus };
      let rows = [];
      if (reportType === 'training') rows = await enablementReportsService?.getTrainingReport(filters);
      else if (reportType === 'certifications') rows = await enablementReportsService?.getCertificationsReport(filters);
      else if (reportType === 'assignments') rows = await enablementReportsService?.getAssignmentsReport(filters);
      else if (reportType === 'capability') rows = await enablementReportsService?.getCapabilityReport(filters);
      setData(rows?.map(r => flattenRow(r, reportType)) || []);
    } catch (err) { toast?.error('Failed to load report.'); }
    finally { setLoading(false); }
  };

  const handleExportCSV = () => {
    if (data?.length === 0) { toast?.error('No data to export.'); return; }
    exportToCSV(data, COLUMNS?.[reportType], HEADERS?.[reportType], `enablement_${reportType}_${new Date()?.toISOString()?.split('T')?.[0]}.csv`);
    toast?.success('CSV exported successfully.');
  };

  const handleExportPDF = () => {
    if (data?.length === 0) { toast?.error('No data to export.'); return; }
    const rt = REPORT_TYPES?.find(r => r?.id === reportType);
    exportToPDF({
      title: `Enablement Report — ${rt?.label}`,
      subtitle: `Partner: ${filterPartner === 'all' ? 'All Partners' : partners?.find(p => p?.id === filterPartner)?.brand_name || 'Selected'} | Status: ${filterStatus === 'all' ? 'All' : filterStatus}`,
      rows: data,
      columns: COLUMNS?.[reportType],
      headers: HEADERS?.[reportType],
      summaryCards: [
        { label: 'Total Records', value: data?.length },
        { label: 'Report Type', value: rt?.label },
        { label: 'Generated', value: new Date()?.toLocaleDateString() },
      ]
    });
  };

  const cols = COLUMNS?.[reportType] || [];
  const headers = HEADERS?.[reportType] || {};

  return (
    <div className="space-y-20">
      {/* Header */}
      <div className="bg-white rounded-2xl border border-slate-200/60 shadow-sm p-24">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-16 mb-20">
          <div className="flex items-center gap-14">
            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-slate-600 to-slate-800 flex items-center justify-center shadow-lg">
              <Icon name="FileBarChart" size={22} className="text-white" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-slate-900">Enablement Reports</h2>
              <p className="text-sm text-slate-500 mt-1">Export training, certification, and capability data</p>
            </div>
          </div>
          <div className="flex items-center gap-10">
            <button onClick={handleExportCSV} className="flex items-center gap-8 px-18 py-10 rounded-xl border border-emerald-200 bg-emerald-50 text-emerald-700 text-sm font-semibold hover:bg-emerald-100 transition-colors">
              <Icon name="Download" size={16} />Export CSV
            </button>
            <button onClick={handleExportPDF} className="flex items-center gap-8 px-18 py-10 rounded-xl bg-gradient-to-r from-slate-600 to-slate-800 text-white text-sm font-semibold shadow-md hover:shadow-lg transition-all">
              <Icon name="FileText" size={16} />Export PDF
            </button>
          </div>
        </div>

        {/* Report Type Selector */}
        <div className="flex flex-wrap gap-10 mb-20">
          {REPORT_TYPES?.map(rt => (
            <button key={rt?.id} onClick={() => { setReportType(rt?.id); setFilterStatus('all'); }} className={`flex items-center gap-8 px-18 py-10 rounded-xl text-sm font-semibold transition-all ${reportType === rt?.id ? `bg-gradient-to-r ${rt?.color} text-white shadow-md` : 'bg-slate-100 text-slate-600 hover:bg-slate-200'}`}>
              <Icon name={rt?.icon} size={16} />{rt?.label}
            </button>
          ))}
        </div>

        {/* Filters */}
        <div className="flex flex-wrap gap-12">
          <select value={filterPartner} onChange={e => setFilterPartner(e?.target?.value)} className="px-14 py-10 text-sm border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-slate-500/20 bg-white">
            <option value="all">All Partners</option>
            {partners?.map(p => <option key={p?.id} value={p?.id}>{p?.brand_name || p?.legal_name}</option>)}
          </select>
          <select value={filterStatus} onChange={e => setFilterStatus(e?.target?.value)} className="px-14 py-10 text-sm border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-slate-500/20 bg-white">
            <option value="all">All Statuses</option>
            {reportType === 'training' && ['required', 'in-progress', 'completed', 'pending']?.map(s => <option key={s} value={s}>{s}</option>)}
            {reportType === 'certifications' && ['active', 'expired', 'pending', 'revoked']?.map(s => <option key={s} value={s}>{s}</option>)}
            {reportType === 'assignments' && ['assigned', 'in_progress', 'completed', 'overdue']?.map(s => <option key={s} value={s}>{s}</option>)}
          </select>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-16">
        {[
          { label: 'Total Records', value: data?.length, icon: 'FileText', color: 'from-slate-500 to-slate-700' },
          { label: 'Partners', value: [...new Set(data?.map(r => r?.partner_name))]?.filter(Boolean)?.length, icon: 'Building2', color: 'from-blue-500 to-indigo-600' },
          { label: 'Completed', value: data?.filter(r => r?.training_status === 'completed' || r?.cert_status === 'active' || r?.assignment_status === 'completed')?.length, icon: 'CheckCircle2', color: 'from-emerald-500 to-teal-600' },
          { label: 'Needs Attention', value: data?.filter(r => r?.training_status === 'required' || r?.cert_status === 'expired' || r?.assignment_status === 'overdue')?.length, icon: 'AlertCircle', color: 'from-red-500 to-rose-600' },
        ]?.map((stat, i) => (
          <div key={i} className="bg-white rounded-2xl border border-slate-200/60 shadow-sm p-20">
            <div className="flex items-center justify-between mb-12">
              <div className={`w-10 h-10 rounded-xl bg-gradient-to-br ${stat?.color} flex items-center justify-center`}><Icon name={stat?.icon} size={18} className="text-white" /></div>
              <span className="text-2xl font-bold text-slate-900">{stat?.value}</span>
            </div>
            <p className="text-xs text-slate-500 font-medium">{stat?.label}</p>
          </div>
        ))}
      </div>

      {/* Data Table */}
      <div className="bg-white rounded-2xl border border-slate-200/60 shadow-sm overflow-hidden">
        <div className="px-24 py-16 border-b border-slate-100 flex items-center justify-between">
          <span className="text-sm font-semibold text-slate-700">{data?.length} records</span>
          <span className="text-xs text-slate-400">{REPORT_TYPES?.find(r => r?.id === reportType)?.label}</span>
        </div>
        {loading ? (
          <div className="flex items-center justify-center py-48"><div className="w-10 h-10 border-4 border-slate-500/20 border-t-slate-600 rounded-full animate-spin" /></div>
        ) : data?.length === 0 ? (
          <div className="p-48 text-center"><Icon name="FileBarChart" size={40} className="text-slate-300 mx-auto mb-16" /><p className="text-slate-500 font-medium">No data for selected filters</p></div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="bg-slate-50 border-b border-slate-200">
                  {cols?.map(col => <th key={col} className="px-20 py-12 text-left text-xs font-semibold text-slate-600 whitespace-nowrap">{headers?.[col] || col}</th>)}
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {data?.slice(0, 50)?.map((row, i) => (
                  <tr key={i} className="hover:bg-slate-50/50 transition-colors">
                    {cols?.map(col => {
                      const val = row?.[col];
                      const isStatus = col?.includes('status');
                      const isScore = col?.includes('score');
                      const isDate = col?.includes('date');
                      return (
                        <td key={col} className="px-20 py-12 text-sm text-slate-700 whitespace-nowrap">
                          {isStatus && val ? <span className={`px-8 py-3 rounded-lg text-xs font-semibold ${statusBadge(val)}`}>{val}</span>
                            : isScore && val !== undefined ? <span className={`font-bold ${val >= 80 ? 'text-emerald-600' : val >= 60 ? 'text-amber-600' : 'text-red-600'}`}>{val}</span>
                            : isDate && val ? new Date(val)?.toLocaleDateString()
                            : val !== null && val !== undefined ? String(val) : '—'}
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
            {data?.length > 50 && <div className="px-24 py-12 text-xs text-slate-400 border-t border-slate-100">Showing 50 of {data?.length} records. Export to see all.</div>}
          </div>
        )}
      </div>
    </div>
  );
};

export default EnablementReports;
